import { Button as InitialButton, ToggleButton, ButtonGroup } from '@frontile/buttons';
import type { ButtonArgs as InitialButtonArgs } from '@frontile/buttons';
import type { TOC } from '@ember/component/template-only';
import { concat } from '@ember/helper';
import { PhArrowsClockwise } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import { and, eq } from '../utilities.ts';

export interface ButtonArgs {
  Args: InitialButtonArgs & {
    isRunning?: boolean;
    // Icon component to display (usually a Phosphor icon)
    icon?: ComponentLike;
    // Text label for the button
    label?: string;
    // Icon placement relative to label
    iconPlacement?: 'start' | 'end';
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}
/* Updates the implementation of how the button is rendered to include a spinner
   when the button is running. Long term we should look at provider context or
   see if a spinner can be added to the main library. The alternative is to make
   a component that just provides the content inside the button. But we also want
   the button to disable when running. */

const Button: TOC<ButtonArgs> = <template>
  <InitialButton
    @type={{@type}}
    @appearance={{@appearance}}
    @intent={{@intent}}
    @size={{@size}}
    @isRenderless={{@isRenderless}}
    @class={{concat @class " relative"}}
    @isInGroup={{@isInGroup}}
    disabled={{if @isRunning true false}}
    ...attributes
  >
    <div
      class="transition-opacity duration-500 ease-in-out
        {{if @isRunning 'opacity-100' 'opacity-0 pointer-events-none'}}
        flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
    >
      <PhArrowsClockwise @size="24" class="animate-spin" />
    </div>
    <div
      class="transition-opacity duration-500 ease-in-out
        {{if @isRunning 'opacity-0 pointer-events-none' 'opacity-100'}}
        flex items-center justify-center gap-1.5"
    >
      {{#if (and @icon @label)}}
        {{! Icon + Label layout }}
        {{#if (eq @iconPlacement "end")}}
          <span class="flex">{{@label}}</span>
          {{#let @icon as |Icon|}}
            <Icon />
          {{/let}}
        {{else}}
          {{#let @icon as |Icon|}}
            <Icon />
          {{/let}}
          <span class="flex">{{@label}}</span>
        {{/if}}
      {{else if @icon}}
        {{! Icon only }}
        {{#let @icon as |Icon|}}
          <Icon />
        {{/let}}
      {{else if @label}}
        {{! Label only }}
        <span class="flex">{{@label}}</span>
      {{else}}
        {{! Default yield block }}
        <span class="flex" aria-hidden="true">{{yield}}</span>
      {{/if}}
    </div>
  </InitialButton>
</template>;

export { Button, ToggleButton, ButtonGroup };
